import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ClipboardList, Star, Target } from "lucide-react";
import type { MatchDto } from "@/types/api";
import { matchesApi } from "@/api/matches";
import { statsApi } from "@/api/stats";
import { useScope } from "@/app/useScope";
import { TeamFlag } from "@/components/TeamFlag";
import { RequireContext, ErrorState, PageLoader, EmptyState } from "@/components/StateViews";
import { Tabs } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { isGroupStage, stageLabel } from "@/lib/stage";
import { formatMatchDate } from "@/lib/utils";

export function MyTipsPage() {
  return (
    <RequireContext>
      <MyTips />
    </RequireContext>
  );
}

function MyTips() {
  const scope = useScope();
  const [tab, setTab] = useState("all");

  const matchesQ = useQuery({
    queryKey: ["matches", scope.leagueId, scope.tournamentId],
    queryFn: () => matchesApi.list(scope),
  });
  const statsQ = useQuery({
    queryKey: ["personalStats", scope.leagueId, scope.tournamentId],
    queryFn: () => statsApi.personal(scope),
  });

  // Csak azok a meccsek, amikre van saját tipp (függő + lepontozott).
  const myTips = useMemo(
    () => (matchesQ.data ?? []).filter((m) => m.myScoreA !== null && m.myScoreB !== null),
    [matchesQ.data],
  );

  const filtered = useMemo(() => {
    if (tab === "group") return myTips.filter((m) => isGroupStage(m.stage));
    if (tab === "knockout") return myTips.filter((m) => !isGroupStage(m.stage));
    return myTips;
  }, [myTips, tab]);

  if (matchesQ.isLoading) return <PageLoader />;
  if (matchesQ.error) return <ErrorState error={matchesQ.error} />;

  const s = statsQ.data;

  return (
    <div className="space-y-4">
      <div>
        <h1 className="flex items-center gap-2 text-xl font-semibold tracking-tight">
          <ClipboardList className="size-5 text-primary" /> Leadott tippjeim
        </h1>
        <p className="text-sm text-muted-foreground">Minden tipped — a függők és a lepontozottak is. A részletes pontbontás a fejléc pontszámára kattintva jön elő.</p>
      </div>

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <SummaryTile label="Összpont" value={s?.totalPoints ?? 0} primary />
        <SummaryTile label="Leadott tipp" value={myTips.length} />
        <SummaryTile label="Pontozott" value={s?.scoredTips ?? 0} />
        <SummaryTile label="Telitalálat" value={s?.bullseyeCount ?? 0} />
      </div>

      <Tabs
        items={[
          { value: "all", label: "Összes" },
          { value: "group", label: "Csoportkör" },
          { value: "knockout", label: "Kieséses" },
        ]}
        value={tab}
        onValueChange={setTab}
      />

      {myTips.length === 0 ? (
        <EmptyState title="Még nincs leadott tipped" description="A Csoportkör vagy a Kieséses oldalon tippelj mérkőzésekre." icon={<ClipboardList className="size-7" />} />
      ) : (
        <div className="space-y-2">
          {filtered.map((m) => (
            <TipCard key={m.id} m={m} />
          ))}
        </div>
      )}
    </div>
  );
}

function SummaryTile({ label, value, primary }: { label: string; value: number; primary?: boolean }) {
  return (
    <div className={cn("rounded-lg border p-3 text-center", primary ? "border-primary/40 bg-primary/10" : "border-border/60 bg-card/50")}>
      <div className={cn("text-xl font-semibold tabular-nums", primary && "text-primary")}>{value}</div>
      <div className="text-[0.65rem] uppercase tracking-wide text-muted-foreground">{label}</div>
    </div>
  );
}

function TipCard({ m }: { m: MatchDto }) {
  const hasResult = m.scoreA !== null && m.scoreB !== null;
  return (
    <div className={cn("rounded-xl border bg-card p-3 shadow-sm", m.isTuti ? "border-warning/50" : "border-border")}>
      <div className="mb-1.5 flex items-center gap-2 text-xs text-muted-foreground">
        <Badge variant="outline">{stageLabel(m.stage)}</Badge>
        <span>{formatMatchDate(m.matchDate)}</span>
        <div className="ml-auto flex items-center gap-1.5">
          {m.isTuti && <Star className="size-3.5 fill-warning text-warning" />}
          {hasResult && m.points !== null && (
            <Badge variant={m.points > 0 ? "default" : "secondary"}>{m.points} pont</Badge>
          )}
          {!hasResult && <Badge variant="outline">függőben</Badge>}
        </div>
      </div>

      <div className="flex items-center gap-2 text-sm">
        <div className="flex min-w-0 flex-1 items-center justify-end gap-1.5 text-right">
          <span className="truncate font-medium">{m.teamAName}</span>
          <TeamFlag name={m.teamAName} flag={m.teamAFlag} className="size-6" />
        </div>
        <div className="shrink-0 text-center">
          <div className="font-semibold tabular-nums">{hasResult ? `${m.scoreA}–${m.scoreB}` : "vs"}</div>
          <div className="flex items-center gap-1 text-xs text-muted-foreground tabular-nums">
            tipp: {m.myScoreA}–{m.myScoreB}
            {hasResult && m.myScoreA === m.scoreA && m.myScoreB === m.scoreB && <Target className="size-3 text-success" />}
          </div>
        </div>
        <div className="flex min-w-0 flex-1 items-center gap-1.5">
          <TeamFlag name={m.teamBName} flag={m.teamBFlag} className="size-6" />
          <span className="truncate font-medium">{m.teamBName}</span>
        </div>
      </div>
    </div>
  );
}
